import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getHqCatalog, getHqPersonAccess, previewHqPerson, applyHqPerson, checkHqRequest,
  listHrAccessGrants,
  HqCatalog, HqPersonAccess, HqPersonPreview, HqEffect,
} from '../../api/client';
import {
  effectiveOf, editorLock, editableKeys, mergedExceptions, modulesOf, roleChoices, roleKeys, rowFor, sameExceptions, summaryLine, toggle,
  describeChange, WRITES_CLOSED_TEXT, vendorLevelOf, baselineWithNamed, planSave, namedImpliedKeys, VENDOR_LEVEL_BY_RANK, VENDOR_LEVEL_LABEL, Exceptions, PersonContext,
} from './hqPermissionsModel';
import { capabilityLabel, scopeLabel, CAPABILITY_EXPLANATION } from '../hr/workspace/accessLabels';
import { refreshHqAccess } from './hqAccess';
import { PermissionGroupCard, GROUP_ROW, GROUP_LABEL_CELL, GROUP_ALLOWED_CELL, GROUP_STATUS_CELL } from './PermissionGroupCard';
import { getUnresolved, recordAttempt, markUnknown, clearAttempt, classifyHqResponse, classifyHqCheck, NO_RECORD_YET_TEXT, lastRecordFailure, notStartedText } from './unresolvedChange';
import { useAttemptStore } from './useAttemptStore';
import { VendorSourcesPanel } from './NamedVendorAccess';

/**
 * BUILD-039 — a person's access. The account card shows ONE short line (role, and how many permissions were added or removed for them) and
 * "Edit permissions". The editor keeps the Role dropdown and shows every Unique HQ permission as a grouped checkbox list, initialised from the role's
 * defaults: ticking or unticking a box makes a per-person exception (marked Custom) without changing the role or anyone else; "Reset to role defaults"
 * removes them. Vendors levels sit in the same list (View <= Maintain <= Administer). HR access is shown READ-ONLY with a link to HR Administration, where it is
 * given and removed with HR's own scopes and rules: this Save never creates or revokes an HR grant. The change is reviewed and saved once. The server decides it
 * again; a lost answer is reported as unknown, never as "nothing changed", and the attempt is kept in this browser until its result is known.
 */
export function HqAccessCard({ employeeId, name, nameOf, viewerIsPrimaryOwner, viewerEmployeeId }: {
  employeeId: string; name: string; active?: boolean; nameOf: (id: string) => string; viewerIsPrimaryOwner: boolean; viewerEmployeeId?: string | null;
}) {
  const [catalog, setCatalog] = useState<HqCatalog | null>(null);
  const [person, setPerson] = useState<HqPersonAccess | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  async function load() {
    const [c, p] = await Promise.all([getHqCatalog().catch(() => null), getHqPersonAccess(employeeId).catch(() => null)]);
    if (!c || !p || !c.ok || !p.ok) { setError((p && !p.ok && p.body?.message) || (c && !c.ok && c.body?.message) || 'Access could not be loaded.'); return; }
    setError(''); setCatalog(c.body); setPerson(p.body);
  }
  useEffect(() => { load(); }, [employeeId]);

  if (error) return <section aria-label="Unique HQ access" className="text-xs text-red-700" role="alert">{error} <button className="underline" onClick={load}>Retry</button></section>;
  if (!catalog || !person) return <section aria-label="Unique HQ access" className="text-xs text-gray-500" role="status">Loading access…</section>;

  const roleLabel = person.role.roles.map((r) => r.label).join(' + ') || 'No role';
  const exc: Exceptions = Object.fromEntries(person.exceptions.map((x) => [x.key, x.effect]));
  return (
    <section aria-label="Unique HQ access" className="text-xs" data-testid="hq-access-card">
      <h6 className="font-medium text-gray-500 mb-1">Unique HQ role</h6>
      <p className="text-gray-800" data-testid="hq-role-current">{summaryLine(roleLabel, exc)}</p>
      <button type="button" onClick={() => setOpen(true)} className="mt-2 px-2.5 py-1 border border-gray-300 rounded-md font-medium text-blue hover:bg-gray-50" data-testid="hq-edit-permissions">
        {person.viewer.canChange ? 'Edit permissions' : 'View permissions'}
      </button>
      {open && (
        <PermissionsEditor catalog={catalog} person={person} name={name} nameOf={nameOf} viewerIsPrimaryOwner={viewerIsPrimaryOwner}
          self={!!viewerEmployeeId && viewerEmployeeId === employeeId} viewerId={viewerEmployeeId ?? null} onClose={() => setOpen(false)} onSaved={async () => { refreshHqAccess(); await load(); }} />
      )}
    </section>
  );
}

type HrGrant = { grantId: string; employeeId: string; capability: string; scope: string };
type Outcome = { kind: 'ok' | 'error' | 'unknown'; text: string };
type ApplyBody = { roleId: string | null; exceptions: Exceptions; expectedVersion: number; previewToken: string; reason?: string; absorbNamedVendor?: string | null };
/**
 * ONE save of a person's access: one request id and one frozen payload, written to durable browser storage BEFORE it is sent (see unresolvedChange.ts). While the
 * record exists nothing can be edited or re-reviewed: a changed payload must never replace an attempt whose result is not known. HR is not part of it.
 */

function PermissionsEditor({ catalog, person, name, nameOf, viewerIsPrimaryOwner, self, viewerId, onClose, onSaved }: {
  catalog: HqCatalog; person: HqPersonAccess; name: string; nameOf: (id: string) => string; viewerIsPrimaryOwner: boolean; self: boolean; viewerId: string | null;
  onClose: () => void; onSaved: () => Promise<void>;
}) {
  const currentRoleId = person.role.source === 'assigned' ? person.role.roles[0]?.roleId ?? null : null;
  const currentExceptions: Exceptions = useMemo(() => Object.fromEntries(person.exceptions.map((x) => [x.key, x.effect])), [person]);
  const [roleId, setRoleId] = useState<string | null>(null);              // null = keep the current role
  // A legacy direct ("named") Vendors grant is one more SOURCE of this same checklist: its levels start ticked. The in-screen 'allow's below are sent only if this save changes Vendors
  // (then the server moves the grant into the save); a save about other permissions leaves the grant and the saved Vendors exceptions exactly as they are.
  const namedGrant = person.namedVendor ?? null;
  const namedLevel = person.namedVendor === undefined ? 'unknown' : person.namedVendor === null ? 'none' : person.namedVendor.level;
  const baseline: Exceptions = useMemo(() => baselineWithNamed(currentExceptions, person.roleDefaults, namedLevel), [currentExceptions, person, namedLevel]);
  const [pending, setPending] = useState<Exceptions>(baseline);
  const [search, setSearch] = useState('');
  const [hrGrants, setHrGrants] = useState<HrGrant[] | null | 'no-access'>(null);   // read-only: what HR says this person holds
  const attemptKey = `person:${person.employeeId}`;
  useAttemptStore();                                                    // re-render when the durable record changes (also once the signed-in actor is known after a reload)
  const attempt = getUnresolved<ApplyBody>(attemptKey);                 // an attempt whose result is not known (survives a reload): nothing else may be started
  const [review, setReview] = useState<HqPersonPreview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const requestIdRef = useRef<string>(crypto.randomUUID());               // ONE id per reviewed change set; reused on retry after a lost answer
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listHrAccessGrants().then((d) => setHrGrants((d.grants as HrGrant[]).filter((g) => g.employeeId === person.employeeId))).catch(() => setHrGrants('no-access'));
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const selectedRole = roleId ? catalog.roles.find((r) => r.roleId === roleId) || null : null;
  const defaults = useMemo(() => new Set(selectedRole ? roleKeys(selectedRole, catalog.permissions) : person.roleDefaults), [selectedRole, person, catalog]);
  const desired = useMemo(() => effectiveOf(defaults, pending), [defaults, pending]);
  const targetPrivileged = person.role.roles.some((r) => r.privileged) || !!selectedRole?.privileged;
  const ctx: PersonContext = { viewer: person.viewer, self, targetPrivileged, targetCompanyAdmin: person.facts.companyAdmin, namedVendorLevel: namedLevel,
    primaryOwner: person.facts.primaryOwner, servicePrincipal: person.facts.servicePrincipal ?? person.facts.protectedIdentity, active: person.facts.status === 'active' };
  const writesClosed = person.writes?.open === false;                   // permission writes are not open on the platform path yet: read-only, truthfully
  const lock = writesClosed ? WRITES_CLOSED_TEXT : editorLock(ctx);
  const editable = editableKeys(catalog.permissions, defaults, desired, ctx);
  const labelOf = (k: string) => catalog.permissions.find((p) => p.key === k)?.label || k;
  const plan = useMemo(() => planSave({ current: currentExceptions, baseline, pending, currentDefaults: person.roleDefaults, defaults, namedGrantId: namedGrant?.grantId ?? null }),
    [currentExceptions, baseline, pending, person, defaults, namedGrant]);
  const hqChanged = roleId !== null || !sameExceptions(plan.exceptions, currentExceptions) || plan.absorb !== null;
  const anyChange = hqChanged;
  const frozen = !!attempt;                                              // an attempt whose result is not known: nothing may be changed or re-reviewed
  // the saved person changed (our own HQ save landed): the draft starts again from what is now saved
  useEffect(() => { setPending(baseline); setRoleId(null); }, [person.version, namedGrant?.grantId]);

  function edit(key: string) {
    if (frozen) return;
    setReview(null); setOutcomes([]);
    const next = toggle(desired, key);
    setPending(mergedExceptions(pending, defaults, next, editable));
  }
  function resetToDefaults() {
    if (frozen) return;
    setReview(null); setOutcomes([]);
    setPending(mergedExceptions(pending, defaults, defaults, editable));
  }
  function pickRole(id: string) {
    if (frozen) return;
    setReview(null); setOutcomes([]);
    setRoleId(id === (currentRoleId ?? '') || id === '' ? null : id);   // existing per-person exceptions are kept and shown in the review
  }

  async function doReview() {
    if (frozen) return;                                                 // a new review must never replace an unresolved attempt
    setBusy(true); setOutcomes([]);
    requestIdRef.current = crypto.randomUUID();                         // a NEW change set gets a new id
    if (hqChanged) {
      const r = await previewHqPerson(person.employeeId, roleId, plan.exceptions, plan.absorb).catch(() => null);
      if (!r || !r.ok) { setBusy(false); setOutcomes([{ kind: 'error', text: r?.body?.message || 'The changes could not be reviewed.' }]); return; }
      setReview(r.body);
    } else {
      setReview({ employeeId: person.employeeId, roleId: null, before: [], after: [], added: [], removed: [], roleDefaults: [], currentExceptions, redundantExceptions: [], version: person.version, previewToken: '' });
    }
    setBusy(false);
  }
  /** Save pressed: freeze the reviewed Unique HQ payload and the planned HR changes into ONE attempt, then send what is unresolved. */
  async function save() {
    if (!review || frozen || !hqChanged || lock) return;
    const body: ApplyBody = { roleId, exceptions: { ...plan.exceptions }, expectedVersion: review.version, previewToken: review.previewToken, reason: reason.trim() || undefined, absorbNamedVendor: plan.absorb };
    // recorded in durable browser storage BEFORE it is sent; if it cannot be recorded (no known actor) nothing is sent
    if (!recordAttempt(attemptKey, { requestId: requestIdRef.current, payload: body, label: `${name}'s access` })) { setOutcomes([{ kind: 'error', text: notStartedText(lastRecordFailure()) }]); return; }
    await send(requestIdRef.current, body, false);
  }
  /** Save again after a lost answer (or after a reload): the SAME request id and the SAME frozen payload, never what is on the screen now. */
  async function saveAgain() {
    if (!attempt) return;
    await send(attempt.requestId, attempt.payload, true);
  }

  /**
   * Send the (already recorded) attempt. Only a recorded outcome, a first send's own definite refusal, or a refusal the platform recorded (fenced) settles it
   * (classifyHqResponse). A generic 500, 429, a malformed body or an ordinary refusal of a RETRY leave it UNKNOWN: the original may still commit. Same request id and
   * payload every time.
   */
  async function send(requestId: string, body: ApplyBody, retry: boolean) {
    setBusy(true);
    const r = await applyHqPerson(requestId, person.employeeId, body).catch(() => null);
    setBusy(false);
    const verdict = classifyHqResponse(r, retry);
    if (!r || verdict === 'unresolved') {
      markUnknown(attemptKey, requestId);
      setOutcomes([{ kind: 'unknown', text: 'Unique HQ permissions: the answer did not settle it, so it is not known whether they were saved. Nothing else can be changed until this is settled. Use "Check", or "Save again (same change)": it is applied once.' }]);
      return;
    }
    clearAttempt(attemptKey, requestId);
    if (verdict === 'refused') { setOutcomes([{ kind: 'error', text: `Unique HQ permissions were not saved: ${r.body?.message || 'refused.'}` }]); return; }
    setOutcomes([{ kind: 'ok', text: r.body.replayed ? 'Unique HQ permissions were already saved by this same change.' : 'Unique HQ permissions saved. They apply within about a minute, also to a session that is already open.' }]);
    setReview(null); setRoleId(null);
    await onSaved();
  }

  /** After a lost answer (or a reload): was MY change recorded? An absent record is "no record yet", never "not saved". */
  async function check() {
    if (!attempt) return;
    const r = await checkHqRequest(attempt.requestId).catch(() => null);
    if (!r || !r.ok) { setOutcomes([{ kind: 'unknown', text: 'Still not known. Try Check again in a moment.' }]); return; }
    const v = classifyHqCheck(r);
    if (v === 'unresolved') { setOutcomes([{ kind: 'unknown', text: NO_RECORD_YET_TEXT }]); return; }
    clearAttempt(attemptKey, attempt.requestId); setReview(null);
    if (v === 'applied') { setOutcomes([{ kind: 'ok', text: 'Checked: the Unique HQ permissions WERE saved.' }]); await onSaved(); }
    else setOutcomes([{ kind: 'error', text: 'Checked: the platform refused this change and recorded the refusal, so it can never be applied. Nothing was saved.' }]);
  }

  const q = search.trim().toLowerCase();
  const modules = modulesOf(catalog.permissions).map((m) => ({ ...m, permissions: m.permissions.filter((p) => !q || p.label.toLowerCase().includes(q) || m.module.toLowerCase().includes(q)) })).filter((m) => m.permissions.length);
  const choices = roleChoices(catalog.roles, person.viewer, currentRoleId, person.role.roles.some((r) => r.privileged));
  const directKeys = new Set(namedImpliedKeys(namedLevel).filter((k) => !defaults.has(k) && !currentExceptions[k]));   // ticked only because of the direct grant
  const isDirect = (k: string) => directKeys.has(k) && pending[k] === 'allow';
  const customCount = Object.keys(pending).filter((k) => !isDirect(k)).length;

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex justify-end" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`Permissions for ${name}`} className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-xl p-5 text-sm focus:outline-none" data-testid="hq-permissions-editor">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-navy">Permissions — {name}</h2>
            <p className="text-xs text-gray-500">Starts from the role. Tick or untick to change this person only. Their Pricing and Project Manager access is not affected.</p>
          </div>
          <button type="button" onClick={onClose} className="px-2 py-1 border rounded text-xs" aria-label="Close permissions">Close</button>
        </div>
        {lock && <p className="mt-3 text-xs text-gray-700 bg-gray-50 border rounded px-3 py-2" data-testid="hq-editor-lock">{lock}</p>}
        {person.facts.lifecycleRetained && !person.facts.servicePrincipal && (
          <p className="mt-3 text-xs text-gray-600 bg-gray-50 border rounded px-3 py-2" data-testid="hq-retained-note">
            This account is kept from being deactivated{person.facts.lifecycleRetained.reason ? `: ${person.facts.lifecycleRetained.reason}` : '.'} That does not limit changing its role or permissions.
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">Role
            <select aria-label="Role" className="block mt-1 border rounded px-2 py-1 text-sm" value={roleId ?? currentRoleId ?? ''} disabled={!!lock || frozen} onChange={(e) => pickRole(e.target.value)} data-testid="hq-role-select">
              {!currentRoleId && <option value="">{person.role.roles.map((r) => r.label).join(' + ') || 'No role'}</option>}
              {choices.map(({ role, disabled }) => <option key={role.roleId} value={role.roleId} disabled={!!disabled} title={disabled || undefined}>{role.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600">Find a permission
            <input aria-label="Find a permission" value={search} onChange={(e) => setSearch(e.target.value)} className="block mt-1 border rounded px-2 py-1 text-sm w-56" />
          </label>
          <span className="text-xs text-gray-600" data-testid="hq-custom-count">{customCount ? `${customCount} custom for ${name}` : 'Matches the role'}</span>
          {!lock && !frozen && customCount > 0 && <button type="button" onClick={resetToDefaults} className="text-xs text-blue underline" data-testid="hq-reset-defaults">Reset to role defaults</button>}
        </div>

        {/* one bordered card per category, the same look as Roles & Permissions */}
        <div className="mt-4 space-y-4" data-testid="hq-permission-list">
            {modules.map((m) => (
              <PermissionGroupCard key={m.module} title={m.module}>
                {m.permissions.map((p) => {
                  const row = rowFor(p, defaults, desired, ctx);
                  return (
                    <tr key={p.key} className={GROUP_ROW} data-testid={`hq-row-${p.key}`}>
                      <th scope="row" className={GROUP_LABEL_CELL}>
                        <div className="text-gray-800">{row.label}</div>
                        <div className="text-[11px] text-gray-500">{row.description}</div>
                      </th>
                      <td className={GROUP_ALLOWED_CELL}>
                        <input type="checkbox" checked={row.on} disabled={!!row.locked || frozen} onChange={() => edit(p.key)} aria-label={`${row.label}: allowed`}
                          title={row.locked || undefined} className="rounded border-gray-300 disabled:opacity-60" data-testid={`hq-check-${p.key}`} />
                      </td>
                      <td className={GROUP_STATUS_CELL}>
                        {row.locked && row.locked !== lock ? <span className="text-gray-500" title={row.locked}>🔒 {shortLock(row.locked)}</span>
                          : isDirect(p.key) ? <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 font-medium" title="Given to this person by name. Saving a change to Vendors moves it into this checklist." data-testid={`hq-direct-${p.key}`}>Direct grant</span>
                          : row.custom ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" data-testid={`hq-custom-${p.key}`}>Custom</span>
                          : <span className="text-gray-400">Role default</span>}
                      </td>
                    </tr>
                  );
                })}
              </PermissionGroupCard>
            ))}
            <HrReadOnly grants={hrGrants} hidden={!!q && !'hr'.includes(q) && !'human resources'.includes(q)} />
        </div>

        <VendorSourcesPanel roleLevel={vendorLevelOf(person.roleDefaults)} hqLevel={vendorLevelOf(effectiveOf(person.roleDefaults, currentExceptions))} companyAdmin={person.facts.companyAdmin}
          named={person.namedVendor === undefined ? 'unknown' : person.namedVendor === null ? null : { ...person.namedVendor }} grantedByName={nameOf} />

        <div className="mt-4 sticky bottom-0 bg-white border-t pt-3 pb-1">
          {review && (
            <div className="mb-3 p-3 border rounded bg-gray-50 text-xs" data-testid="hq-review">
              <p className="font-medium text-gray-700 mb-1">Review</p>
              {selectedRole && <p>Role: {person.role.roles.map((r) => r.label).join(' + ') || 'No role'} → {selectedRole.label}</p>}
              {hqChanged && <ul className="list-disc pl-4 text-gray-700">{describeChange(review, labelOf).map((l) => <li key={l}>{l}</li>)}</ul>}
              {hqChanged && typeof review.vendorBefore === 'number' && typeof review.vendorAfter === 'number' && (review.vendorBefore !== review.vendorAfter || plan.absorb) && (
                <ul className="list-disc pl-4 text-gray-700 mt-1" data-testid="hq-vendor-retention">
                  <li data-testid="hq-vendor-final">Final Vendors access (all sources): {VENDOR_LEVEL_LABEL[VENDOR_LEVEL_BY_RANK[review.vendorBefore]]} → {VENDOR_LEVEL_LABEL[VENDOR_LEVEL_BY_RANK[review.vendorAfter]]}.</li>
                  {review.companyAdmin && <li>Company Admin authority (Administer) is protected and always applies.</li>}
                  {plan.absorb && review.named && (
                    <li data-testid="hq-vendor-absorb">The direct Vendors grant ({VENDOR_LEVEL_LABEL[review.named.level as 'view' | 'maintain' | 'administer']}, given {review.named.grantedBy ? `by ${nameOf(review.named.grantedBy)} ` : ''}{review.named.grantedAt ? `on ${new Date(review.named.grantedAt).toLocaleDateString()}` : ''}) is moved into this checklist in the same save. Its record is kept in the history.</li>
                  )}
                </ul>
              )}
              {review.redundantExceptions.length > 0 && <p className="text-gray-600 mt-1">Now the same as the role: {review.redundantExceptions.map(labelOf).join(', ')}.</p>}
              <label className="block mt-2 text-gray-600">Reason (optional)
                <input aria-label="Reason (optional)" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} className="block mt-1 border rounded px-2 py-1 w-full text-sm" />
              </label>
            </div>
          )}
          {attempt && outcomes.length === 0 && (
            <p className="mb-3 text-xs text-amber-800" role="alert" data-testid="hq-attempt-open">A save of this person's access is not finished. Nothing can be changed until it is settled.</p>
          )}
          {outcomes.length > 0 && (
            <ul className="mb-3 space-y-1 text-xs" data-testid="hq-outcomes">
              {outcomes.map((o, i) => <li key={i} role={o.kind === 'ok' ? 'status' : 'alert'} className={o.kind === 'ok' ? 'text-green-700' : o.kind === 'unknown' ? 'text-amber-800' : 'text-red-700'}>{o.text}</li>)}
            </ul>
          )}
          <div className="flex gap-2">
            {attempt ? (
              <>
                <button type="button" disabled={busy} onClick={check} className="px-3 py-1.5 border rounded text-xs" data-testid="hq-check-outcome">Check</button>
                <button type="button" disabled={busy} onClick={saveAgain} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="hq-save-again">Save again (same change)</button>
              </>
            ) : !review ? (
              <button type="button" disabled={busy || !!lock || !anyChange} onClick={doReview} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="hq-review-changes">Review changes</button>
            ) : (
              <button type="button" disabled={busy} onClick={save} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="hq-save">Save</button>
            )}
            {!attempt && <button type="button" disabled={busy} onClick={() => { setPending(baseline); setRoleId(null); setReview(null); setOutcomes([]); }} className="px-3 py-1.5 border rounded text-xs">Discard changes</button>}
          </div>
        </div>

        {person.history.length > 0 && (
          <details className="mt-4 text-xs">
            <summary className="cursor-pointer font-medium text-gray-500">Change history</summary>
            <ul className="mt-1 space-y-0.5 text-gray-600">
              {person.history.slice(0, 15).map((h) => <li key={h.seq}>{new Date(h.at).toLocaleString()} — {historyText(h, nameOf, catalog, labelOf)}</li>)}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

function shortLock(reason: string): string {
  if (/Owner only/.test(reason)) return 'Owner only';
  if (/Company Admins always/.test(reason)) return 'Company Admin';
  if (/Company Admin can/.test(reason)) return 'Company Admin decides';
  if (/Given by name/.test(reason)) return 'Named access';
  if (/Named Vendors access could not be read/.test(reason)) return 'Unknown';
  return 'Locked';
}


/**
 * HR access is shown READ-ONLY here: what HR itself says this person holds (capability + scope, one row per real grant). This screen never creates or revokes an HR
 * grant, and Save never calls HR. HR access is given and removed in HR Administration, with HR's own scopes and rules. (A single central place to edit HR access is a
 * recorded follow-up; it needs request correlation owned by the HR service.)
 */
function HrReadOnly({ grants, hidden }: { grants: HrGrant[] | null | 'no-access'; hidden: boolean }) {
  if (hidden) return null;
  const row = (key: string, label: ReactNode, status: ReactNode, testId?: string) => (
    <tr key={key} className={GROUP_ROW} data-testid={testId}>
      <th scope="row" className={GROUP_LABEL_CELL}>{label}</th>
      <td className={GROUP_ALLOWED_CELL} />
      <td className={GROUP_STATUS_CELL}>{status}</td>
    </tr>
  );
  const link = <a href="/hr/admin" className="text-blue underline" data-testid="hq-hr-admin-link">Open HR Administration</a>;
  return (
    <PermissionGroupCard title="HR (read-only here)">
      {grants === null
        ? row('loading', <span className="text-xs text-gray-500">Loading…</span>, null)
        : grants === 'no-access'
          ? row('no-access', <span className="text-xs text-gray-500" data-testid="hq-hr-no-access">HR access cannot be shown to you here. It is given in HR Administration by the people who manage HR access.</span>, link)
          : grants.length === 0
            ? row('none', <span className="text-xs text-gray-600" data-testid="hq-hr-none">No HR access is held by this person.</span>, link)
            : grants.map((g) => row(g.grantId, (
              <>
                <div className="text-gray-800">{capabilityLabel(g.capability)} — {scopeLabel(g.scope)}</div>
                <div className="text-[11px] text-gray-500">{CAPABILITY_EXPLANATION[g.capability]}</div>
              </>
            ), <span className="text-gray-500">Held</span>, `hq-hr-grant-${g.capability}-${g.scope}`))}
      <tr className={GROUP_ROW}>
        <td colSpan={3} className="py-2 px-4 text-[11px] text-gray-600" data-testid="hq-hr-note">
          Shown for information. HR access is given and removed in HR Administration, not from this Save. {link}
        </td>
      </tr>
    </PermissionGroupCard>
  );
}

function historyText(h: HqPersonAccess['history'][number], nameOf: (id: string) => string, catalog: HqCatalog, labelOf: (k: string) => string): string {
  const who = nameOf(h.actorEmployeeId);
  const role = (code: string) => catalog.roles.find((r) => r.code === code)?.label || code;
  if (h.action === 'person-role-set') return `${who} set the role to ${role(h.detail?.toRole || '')}`;
  if (h.action === 'exception-allow') return `${who} allowed "${labelOf(h.permission || '')}"`;
  if (h.action === 'exception-block') return `${who} removed "${labelOf(h.permission || '')}"`;
  if (h.action === 'exception-cleared') return `${who} reset "${labelOf(h.permission || '')}" to the role default`;
  if (h.action === 'named-vendor-absorbed') return `${who} moved the direct Vendors grant (${h.detail?.level || 'level unknown'}${h.detail?.grantedBy ? `, given by ${nameOf(h.detail.grantedBy)}` : ''}) into the checklist`;
  return `${who}: ${h.action}`;
}
